import QRCode from "qrcode";
import { createResource, type JSX, Show, Suspense } from "solid-js";

/**
 * Renders a scannable QR code image from arbitrary text (here, an `otpauth://`
 * URL for TOTP enrollment). Uses the `qrcode` library to rasterize to a PNG
 * data-URL — no network, fully local. While encoding (or on failure) it renders
 * a neutral placeholder so the layout never jumps.
 */
export function QrCode(props: { value: string; size?: number; alt?: string }) {
  const [dataUrl] = createResource(
    () => ({ value: props.value, size: props.size ?? 200 }),
    async ({ value, size }) => {
      try {
        return await QRCode.toDataURL(value, {
          width: size,
          margin: 1,
          errorCorrectionLevel: "M",
        });
      } catch {
        return "";
      }
    },
  );

  const placeholder = (): JSX.Element => (
    <div
      class="animate-pulse rounded-xl bg-muted"
      style={{ width: `${props.size ?? 200}px`, height: `${props.size ?? 200}px` }}
      aria-hidden="true"
    />
  );

  // OWN Suspense boundary: `createResource` integrates with Suspense, so reading
  // the still-loading `dataUrl()` would otherwise bubble up and trip the NEAREST
  // ancestor `<Suspense>` — which in this app is the root one wrapping the whole
  // Outlet. That momentarily swaps the entire route subtree for its `null`
  // fallback and back, detaching+reattaching the page's scroll container and
  // resetting its scrollTop to 0 (the "jump to top" when MFA enrollment starts).
  // Catching the suspension HERE keeps it local: the placeholder shows in place
  // and nothing above us re-mounts.
  return (
    <Suspense fallback={placeholder()}>
      <Show when={dataUrl()} fallback={placeholder()}>
        {(src) => (
          <img
            src={src()}
            alt={props.alt ?? ""}
            width={props.size ?? 200}
            height={props.size ?? 200}
            class="rounded-xl border border-border bg-white p-2"
            data-testid="mfa-qr"
          />
        )}
      </Show>
    </Suspense>
  );
}
